/**
 * Client-side alerts computed from the PlayerView (§3: "the client computes the alert
 * itself"), plus city naming that mirrors `createMatch` and the bot callsigns every lobby
 * uses (server rooms and offline matches alike).
 */
import { TICK_HZ, getGeo, landHexAt, type EntityId, type HexIdx, type MissileKind, type PlayerId, type PlayerInfo, type PlayerView, type Tick, type WorldMap } from '@sa/sim';

/**
 * Names of the cities placed at match start, by hex: map cities outside the used start
 * cores, plus every player's capital and towns (real city name if the map has one there,
 * else the region-derived name `createMatch` uses). Cities built during the match are unnamed.
 */
export function cityNames(world: WorldMap, players: readonly Pick<PlayerInfo, 'regionId'>[]): Map<HexIdx, string> {
  const used = new Set(players.map((p) => p.regionId));
  const byHex = new Map<HexIdx, string>();
  for (const c of world.cities) byHex.set(c.hex, c.name);
  const out = new Map<HexIdx, string>();
  for (const c of world.cities) if (!c.region || !used.has(c.region)) out.set(c.hex, c.name);
  for (const reg of world.regions) {
    if (!used.has(reg.id)) continue;
    out.set(reg.capitalHex, byHex.get(reg.capitalHex) ?? reg.name);
    reg.townHexes.forEach((h, i) => out.set(h, byHex.get(h) ?? `${reg.name.slice(0, 8)} ${i + 1}`));
  }
  return out;
}

/** Bot callsigns, one per lobby slot (slot 1 is KESTREL, … slot 0 CORSAIR): the same names online and offline. */
export const BOT_NAMES = ['CORSAIR', 'KESTREL', 'VULCAN', 'HALCYON', 'OSPREY', 'TALON', 'NOMAD', 'SPECTRE'] as const;

/** The callsign of a bot in lobby slot `slot` (0-based). */
export function botCallsign(slot: number): string {
  return BOT_NAMES[((slot % BOT_NAMES.length) + BOT_NAMES.length) % BOT_NAMES.length];
}

export interface InboundAlert {
  missileId: EntityId;
  kind: MissileKind;
  owner: PlayerId;
  impactTick: Tick;
  /** whole seconds until impact, ≥ 0 (for "m:ss") */
  secondsLeft: number;
  x: number;
  y: number;
  /** land hex of the impact point */
  hex: HexIdx;
  /** nearest own / allied city within CITY_ALERT_RADIUS mu of the impact, if any */
  cityId: EntityId | null;
  cityName: string | null;
}

/** A city counts as the alert target if the impact point is within this distance (mu). */
export const CITY_ALERT_RADIUS = 4;

/**
 * The allies that still bind us: none once we are out (a spectator has no side). The
 * reducer already drops allies that were knocked out; this also covers a view mid-update.
 */
export function activeAllies(view: Pick<PlayerView, 'allies'> & { eliminated?: boolean }): readonly PlayerId[] {
  return view.eliminated ? [] : view.allies;
}

/** Is `owner` us or one of our (active) allies? (0 = nobody is never friendly.) */
export function isFriendlyTo(view: Pick<PlayerView, 'me' | 'allies'> & { eliminated?: boolean }, owner: PlayerId): boolean {
  return owner !== 0 && (owner === view.me || activeAllies(view).includes(owner));
}

/**
 * Earliest-impact hostile tracked missile (not ours, not an ally's) whose target lies in own
 * or allied territory (as the view knows it), or null. `names` (from `cityNames`) resolves
 * the target city name.
 */
export function inboundAlert(view: PlayerView, world: WorldMap, names?: ReadonlyMap<HexIdx, string>, tick: Tick = view.tick): InboundAlert | null {
  const geo = getGeo(world);
  const friendly = (o: PlayerId) => isFriendlyTo(view, o);
  let best: InboundAlert | null = null;
  for (const t of view.tracks.values()) {
    const impact = t.launchTick + t.flightTicks;
    if (impact < tick || (best && impact >= best.impactTick)) continue;
    // our own (or an ally's) launch at our own land is friendly fire, not an inbound threat
    if (friendly(t.owner)) continue;
    const hex = landHexAt(geo, t.tx, t.ty);
    if (hex < 0 || !friendly(view.owner[hex])) continue;
    best = {
      missileId: t.id,
      kind: t.kind,
      owner: t.owner,
      impactTick: impact,
      secondsLeft: Math.max(0, Math.ceil((impact - tick) / TICK_HZ)),
      x: t.tx,
      y: t.ty,
      hex,
      cityId: null,
      cityName: null,
    };
  }
  if (!best) return null;
  let bestD = CITY_ALERT_RADIUS * CITY_ALERT_RADIUS;
  for (const e of view.entities.values()) {
    if (e.kind !== 'city' || !friendly(e.owner)) continue;
    const d = (e.x - best.x) ** 2 + (e.y - best.y) ** 2;
    if (d > bestD) continue;
    bestD = d;
    best.cityId = e.id;
    best.cityName = names?.get(landHexAt(geo, e.x, e.y)) ?? null;
  }
  return best;
}
